import { startImpersonation } from "../../api/impersonation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { formatTimeAgo } from "../../lib/format";
import {
  banIp,
  fetchAdminUsers,
  fetchIpBans,
  resetAdminUserCheckout,
  terminateConnection,
  unbanIp,
  updateAdminUserRoles,
  type AdminConnection,
  type AdminUser,
  type IpBan,
} from "../api";
import { useAdminLive } from "../hooks/useAdminLive";

const KNOWN_ROLES = ["admin", "customer"] as const;

type SortColumn = "id" | "name" | "email" | "roles" | "created" | "pixels" | "status" | "sessions";
type SortDirection = "asc" | "desc";

type UserFilters = Record<SortColumn, string>;

const EMPTY_FILTERS: UserFilters = {
  id: "",
  name: "",
  email: "",
  roles: "",
  created: "",
  pixels: "",
  status: "",
  sessions: "",
};

function matchesRegexFilter(value: string, pattern: string): boolean {
  const trimmed = pattern.trim();
  if (!trimmed) return true;
  try {
    return new RegExp(trimmed, "i").test(value);
  } catch {
    return false;
  }
}

function compareStrings(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function UserRolesEditor({
  user,
  busy,
  lockedRoles = [],
  onChange,
}: {
  user: AdminUser;
  busy: boolean;
  lockedRoles?: string[];
  onChange: (roles: string[]) => void;
}) {
  const roles = new Set(user.roles ?? []);
  const locked = new Set(lockedRoles);

  const toggle = (role: string) => {
    const next = new Set(roles);
    if (next.has(role)) {
      if (next.size === 1 || locked.has(role)) return;
      next.delete(role);
    } else {
      next.add(role);
    }
    onChange([...next]);
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem 0.65rem" }}>
      {KNOWN_ROLES.map((role) => (
        <label
          key={role}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.3rem",
            fontSize: "0.82rem",
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.6 : 1,
          }}
        >
          <input
            type="checkbox"
            checked={roles.has(role)}
            disabled={
              busy ||
              (roles.has(role) && (roles.size === 1 || locked.has(role)))
            }
            onChange={() => toggle(role)}
          />
          {role}
        </label>
      ))}
    </div>
  );
}

function isGuestConnection(c: AdminConnection): boolean {
  return c.id <= 0 || c.username === "guest";
}

function connectionLocation(c: AdminConnection): string {
  const parts = [c.geodata?.city, c.geodata?.country].filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

function BanIpModal({
  ip,
  onCancel,
  onConfirm,
  busy,
}: {
  ip: string;
  onCancel: () => void;
  onConfirm: (comment: string) => void;
  busy: boolean;
}) {
  const [comment, setComment] = useState("");

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        className="admin-panel"
        style={{ width: "min(420px, 92vw)", padding: "1.25rem" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 0.5rem" }}>Ban IP</h3>
        <p className="admin-page-sub" style={{ margin: "0 0 1rem" }}>
          Block <code>{ip}</code> from reconnecting and disconnect all live sessions
          from this address.
        </p>
        <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.85rem" }}>
          Comment
        </label>
        <textarea
          className="admin-input"
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Reason for ban…"
          style={{ width: "100%", resize: "vertical", marginBottom: "1rem" }}
        />
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <button
            type="button"
            className="admin-btn admin-btn-ghost"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-danger"
            disabled={busy}
            onClick={() => onConfirm(comment)}
          >
            Ban IP
          </button>
        </div>
      </div>
    </div>
  );
}

function SessionActions({
  conn,
  busyId,
  busyIp,
  nowMs,
  onKick,
  onBanIp,
}: {
  conn: AdminConnection;
  busyId: string | number | null;
  busyIp: string | null;
  nowMs: number;
  onKick: (conn: AdminConnection) => void;
  onBanIp: (conn: AdminConnection) => void;
}) {
  const ip = conn.ip_address;
  const kicking = busyId === conn.websocket_id;
  const banning = Boolean(ip && busyIp === ip);

  return (
    <div style={{ marginBottom: 4 }}>
      <span style={{ fontSize: "0.8rem", color: "#8ba3b8" }}>
        {ip ?? "?"}
        {conn.geodata?.city ? ` · ${conn.geodata.city}` : ""}
        {conn.seen ? ` · ${formatTimeAgo(conn.seen, nowMs)}` : ""}
      </span>{" "}
      <button
        type="button"
        className="admin-btn admin-btn-danger"
        style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem" }}
        disabled={kicking || banning}
        onClick={() => void onKick(conn)}
      >
        Kick
      </button>{" "}
      {ip ? (
        <button
          type="button"
          className="admin-btn admin-btn-danger"
          style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem" }}
          disabled={kicking || banning}
          onClick={() => onBanIp(conn)}
        >
          Ban IP
        </button>
      ) : null}
    </div>
  );
}

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [bans, setBans] = useState<IpBan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | number | null>(null);
  const [busyIp, setBusyIp] = useState<string | null>(null);
  const [busyRoleUserId, setBusyRoleUserId] = useState<number | null>(null);
  const [busyImpersonationId, setBusyImpersonationId] = useState<number | null>(null);
  const [busyCheckoutUserId, setBusyCheckoutUserId] = useState<number | null>(null);
  const [banTarget, setBanTarget] = useState<AdminConnection | null>(null);
  const [tick, setTick] = useState(0);
  const [filters, setFilters] = useState<UserFilters>(EMPTY_FILTERS);
  const [sortColumn, setSortColumn] = useState<SortColumn>("created");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const { connections, refresh, pulse } = useAdminLive();

  const nowMs = useMemo(() => Date.now(), [pulse, tick]);

  const load = useCallback(async () => {
    try {
      const [userRows, banRows] = await Promise.all([
        fetchAdminUsers(),
        fetchIpBans(),
      ]);
      setUsers(userRows);
      setBans(banRows);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => setTick((n) => n + 1), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const guestConnections = useMemo(
    () => connections.filter(isGuestConnection),
    [connections],
  );

  const onlineUserIds = useMemo(
    () =>
      new Set(
        connections.filter((c) => !isGuestConnection(c)).map((c) => c.id),
      ),
    [connections],
  );

  const connectionsByUser = useMemo(() => {
    const map = new Map<number, AdminConnection[]>();
    for (const c of connections) {
      if (isGuestConnection(c)) continue;
      const list = map.get(c.id) ?? [];
      list.push(c);
      map.set(c.id, list);
    }
    return map;
  }, [connections]);

  const bannedIpSet = useMemo(
    () => new Set(bans.map((b) => b.ip_address)),
    [bans],
  );

  const toggleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortIndicator = (column: SortColumn) => {
    if (sortColumn !== column) return "";
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  const filteredUsers = useMemo(() => {
    const rows = users.map((u) => {
      const live = onlineUserIds.has(u.user_id);
      const sessionCount = (connectionsByUser.get(u.user_id) ?? []).length;
      return { user: u, live, sessionCount };
    });

    const filtered = rows.filter(({ user: u, live, sessionCount }) => {
      const statusLabel = live ? "online" : "offline";
      return (
        matchesRegexFilter(String(u.user_id), filters.id) &&
        matchesRegexFilter(u.name || "", filters.name) &&
        matchesRegexFilter(u.email || "", filters.email) &&
        matchesRegexFilter((u.roles ?? []).join(", "), filters.roles) &&
        matchesRegexFilter(u.created_at ? new Date(u.created_at).toLocaleString() : "", filters.created) &&
        matchesRegexFilter(String(u.bought_pixels ?? 0), filters.pixels) &&
        matchesRegexFilter(statusLabel, filters.status) &&
        matchesRegexFilter(String(sessionCount), filters.sessions)
      );
    });

    const dir = sortDirection === "asc" ? 1 : -1;
    filtered.sort((a, b) => {
      let cmp = 0;
      switch (sortColumn) {
        case "id":
          cmp = a.user.user_id - b.user.user_id;
          break;
        case "name":
          cmp = compareStrings(a.user.name || "", b.user.name || "");
          break;
        case "email":
          cmp = compareStrings(a.user.email || "", b.user.email || "");
          break;
        case "roles":
          cmp = compareStrings(
            (a.user.roles ?? []).join(", "),
            (b.user.roles ?? []).join(", "),
          );
          break;
        case "created":
          cmp = (a.user.created_at ? Date.parse(a.user.created_at) : 0) -
            (b.user.created_at ? Date.parse(b.user.created_at) : 0);
          break;
        case "pixels":
          cmp = (a.user.bought_pixels ?? 0) - (b.user.bought_pixels ?? 0);
          break;
        case "status":
          cmp = Number(a.live) - Number(b.live);
          break;
        case "sessions":
          cmp = a.sessionCount - b.sessionCount;
          break;
      }
      return (cmp || a.user.user_id - b.user.user_id) * dir;
    });

    return filtered;
  }, [
    users,
    onlineUserIds,
    connectionsByUser,
    filters,
    sortColumn,
    sortDirection,
  ]);

  const onRoleChange = async (userId: number, roles: string[]) => {
    setBusyRoleUserId(userId);
    try {
      await updateAdminUserRoles(userId, roles);
      setUsers((prev) =>
        prev.map((u) => (u.user_id === userId ? { ...u, roles } : u)),
      );
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyRoleUserId(null);
    }
  };

  const onResetCheckout = async (user: AdminUser) => {
    if (!window.confirm(
      `Reset all pending payments for ${user.name || user.email}? The selected pixels will become available again, while the user's cart remains intact.`,
    )) return;

    setBusyCheckoutUserId(user.user_id);
    try {
      const result = await resetAdminUserCheckout(user.user_id);
      await load();
      window.alert(
        `${result.orders_aborted} pending order(s) aborted and ${result.pixels_released} pixel(s) released. Ask the user to log out and back in before retrying.`,
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyCheckoutUserId(null);
    }
  };

  const onKick = async (conn: AdminConnection) => {
    setBusyId(conn.websocket_id);
    try {
      await terminateConnection(conn.websocket_id);
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const onBanIp = (conn: AdminConnection) => {
    if (!conn.ip_address) return;
    setBanTarget(conn);
  };

  const confirmBanIp = async (comment: string) => {
    const ip = banTarget?.ip_address;
    if (!ip) return;
    setBusyIp(ip);
    try {
      await banIp(ip, comment.trim());
      setBanTarget(null);
      await Promise.all([refresh(), load()]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyIp(null);
    }
  };

  const onUnban = async (ip: string) => {
    setBusyIp(ip);
    try {
      await unbanIp(ip);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyIp(null);
    }
  };

  return (
    <div>
      <h1 className="admin-page-title">Users</h1>
      <p className="admin-page-sub">
        Registered accounts, live WebSocket sessions, and IP bans.
      </p>

      {error ? <p className="admin-error">{error}</p> : null}

      <div className="admin-toolbar">
        <button type="button" className="admin-btn admin-btn-ghost" onClick={() => void load()}>
          Refresh
        </button>
        <span className="admin-badge admin-badge-live">
          {onlineUserIds.size} user{onlineUserIds.size === 1 ? "" : "s"} online
        </span>
        {guestConnections.length > 0 ? (
          <span className="admin-badge admin-badge-muted">
            {guestConnections.length} guest
            {guestConnections.length === 1 ? "" : "s"}
          </span>
        ) : null}
        <span className="admin-badge admin-badge-muted">
          {filteredUsers.length} / {users.length} shown
        </span>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th><button type="button" className="admin-table-sort" onClick={() => toggleSort("id")}>ID{sortIndicator("id")}</button></th>
              <th>
                <button
                  type="button"
                  className="admin-table-sort"
                  onClick={() => toggleSort("name")}
                >
                  User{sortIndicator("name")}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className="admin-table-sort"
                  onClick={() => toggleSort("email")}
                >
                  Email{sortIndicator("email")}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className="admin-table-sort"
                  onClick={() => toggleSort("roles")}
                >
                  Roles{sortIndicator("roles")}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className="admin-table-sort"
                  onClick={() => toggleSort("created")}
                >
                  Registered{sortIndicator("created")}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className="admin-table-sort"
                  onClick={() => toggleSort("pixels")}
                >
                  Pixels{sortIndicator("pixels")}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className="admin-table-sort"
                  onClick={() => toggleSort("status")}
                >
                  Status{sortIndicator("status")}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className="admin-table-sort"
                  onClick={() => toggleSort("sessions")}
                >
                  Sessions{sortIndicator("sessions")}
                </button>
              </th>
              <th>Aktionen</th>
            </tr>
            <tr className="admin-table-filters">
              <th><input className="admin-input admin-table-filter" value={filters.id} onChange={e => setFilters(f => ({ ...f, id: e.target.value }))} placeholder="ID" aria-label="Filter by customer ID" /></th>
              <th>
                <input
                  className="admin-input admin-table-filter"
                  value={filters.name}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="^Roland"
                  aria-label="Filter by name"
                />
              </th>
              <th>
                <input
                  className="admin-input admin-table-filter"
                  value={filters.email}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, email: e.target.value }))
                  }
                  placeholder="@gmail"
                  aria-label="Filter by email"
                />
              </th>
              <th>
                <input
                  className="admin-input admin-table-filter"
                  value={filters.roles}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, roles: e.target.value }))
                  }
                  placeholder="admin"
                  aria-label="Filter by roles"
                />
              </th>
              <th>
                <input
                  className="admin-input admin-table-filter"
                  value={filters.created}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, created: e.target.value }))
                  }
                  placeholder="Date…"
                  aria-label="Filter by registration date"
                />
              </th>
              <th>
                <input
                  className="admin-input admin-table-filter"
                  value={filters.pixels}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, pixels: e.target.value }))
                  }
                  placeholder="^[1-9]"
                  aria-label="Filter by pixel count"
                />
              </th>
              <th>
                <input
                  className="admin-input admin-table-filter"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, status: e.target.value }))
                  }
                  placeholder="online"
                  aria-label="Filter by status"
                />
              </th>
              <th>
                <input
                  className="admin-input admin-table-filter"
                  value={filters.sessions}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, sessions: e.target.value }))
                  }
                  placeholder="^[1-9]"
                  aria-label="Filter by session count"
                />
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={9}>No registered users.</td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={9}>No users match the current filters.</td>
              </tr>
            ) : (
              filteredUsers.map(({ user: u, live, sessionCount }) => {
                const conns = connectionsByUser.get(u.user_id) ?? [];
                const self = currentUser.id === u.user_id;
                return (
                  <tr key={u.user_id}>
                    <td>{u.user_id}</td>
                    <td>{u.name || "—"}</td>
                    <td>{u.email}</td>
                    <td>
                      <UserRolesEditor
                        user={u}
                        busy={busyRoleUserId === u.user_id}
                        lockedRoles={self ? ["admin"] : []}
                        onChange={(roles) => void onRoleChange(u.user_id, roles)}
                      />
                    </td>
                    <td>
                      {u.created_at ? new Date(u.created_at).toLocaleString() : "—"}
                    </td>
                    <td>
                      <Link className="admin-pixel-link" to={`/admin/pixels?buyer=${u.user_id}`}>
                        {u.bought_pixels ?? 0}
                      </Link>
                    </td>
                    <td>
                      {live ? (
                        <span className="admin-badge admin-badge-live">Online</span>
                      ) : (
                        <span className="admin-badge admin-badge-muted">Offline</span>
                      )}
                    </td>
                    <td>
                      {sessionCount === 0
                        ? "—"
                        : conns.map((c) => (
                            <SessionActions
                              key={c.websocket_id}
                              conn={c}
                              busyId={busyId}
                              busyIp={busyIp}
                              nowMs={nowMs}
                              onKick={onKick}
                              onBanIp={onBanIp}
                            />
                          ))}
                    </td>
                    <td>
                      {!self && !(u.roles ?? []).includes("admin") && (
                        <button type="button" className="admin-btn admin-btn-ghost"
                          disabled={busyImpersonationId !== null}
                          onClick={async () => {
                            setBusyImpersonationId(u.user_id);
                            setError(null);
                            try { await startImpersonation(u.user_id); }
                            catch { setError("Benutzerwechsel fehlgeschlagen. Bitte erneut versuchen."); setBusyImpersonationId(null); }
                          }}>
                          {busyImpersonationId === u.user_id ? "Wechsel läuft …" : "Als Benutzer anmelden"}
                        </button>
                      )}
                      {(u.pending_orders ?? 0) > 0 ? (
                        <button
                          type="button"
                          className="admin-btn admin-btn-danger"
                          disabled={busyCheckoutUserId === u.user_id}
                          onClick={() => void onResetCheckout(u)}
                        >
                          Reset payment ({u.pending_orders})
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <section style={{ marginTop: "2.5rem" }}>
        <h2 className="admin-page-title" style={{ fontSize: "1.35rem" }}>
          Guests
        </h2>
        <p className="admin-page-sub">
          Anonymous browser sessions (not logged in). Guests cannot purchase pixels.
        </p>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Session</th>
                <th>IP</th>
                <th>Location</th>
                <th>Seen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {guestConnections.length === 0 ? (
                <tr>
                  <td colSpan={5}>No guest sessions.</td>
                </tr>
              ) : (
                guestConnections.map((c) => (
                  <tr key={c.websocket_id}>
                    <td style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                      {c.session_id
                        ? c.session_id.length > 12
                          ? `${c.session_id.slice(0, 12)}…`
                          : c.session_id
                        : "—"}
                    </td>
                    <td>
                      {c.ip_address ?? "—"}
                      {c.ip_address && bannedIpSet.has(c.ip_address) ? (
                        <span className="admin-badge admin-badge-muted" style={{ marginLeft: 6 }}>
                          banned
                        </span>
                      ) : null}
                    </td>
                    <td>{connectionLocation(c)}</td>
                    <td>
                      {c.seen ? formatTimeAgo(c.seen, nowMs) : "—"}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="admin-btn admin-btn-danger"
                        style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem", marginRight: 4 }}
                        disabled={busyId === c.websocket_id || busyIp === c.ip_address}
                        onClick={() => void onKick(c)}
                      >
                        Kick
                      </button>
                      {c.ip_address ? (
                        <button
                          type="button"
                          className="admin-btn admin-btn-danger"
                          style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem" }}
                          disabled={busyId === c.websocket_id || busyIp === c.ip_address}
                          onClick={() => onBanIp(c)}
                        >
                          Ban IP
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section style={{ marginTop: "2.5rem" }}>
        <h2 className="admin-page-title" style={{ fontSize: "1.35rem" }}>
          Banned IPs
        </h2>
        <p className="admin-page-sub">
          Blocked addresses cannot open new WebSocket connections.
        </p>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>IP</th>
                <th>Banned</th>
                <th>By</th>
                <th>Comment</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {bans.length === 0 ? (
                <tr>
                  <td colSpan={5}>No banned IPs.</td>
                </tr>
              ) : (
                bans.map((b) => (
                  <tr key={b.ip_address}>
                    <td style={{ fontFamily: "monospace" }}>{b.ip_address}</td>
                    <td>
                      {b.banned_at
                        ? new Date(b.banned_at).toLocaleString()
                        : "—"}
                    </td>
                    <td>
                      {b.banned_by_name || b.banned_by_email || (b.banned_by_user_id != null ? `#${b.banned_by_user_id}` : "—")}
                    </td>
                    <td>{b.comment || "—"}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-btn admin-btn-ghost"
                        style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem" }}
                        disabled={busyIp === b.ip_address}
                        onClick={() => void onUnban(b.ip_address)}
                      >
                        Unban
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {banTarget?.ip_address ? (
        <BanIpModal
          ip={banTarget.ip_address}
          busy={busyIp === banTarget.ip_address}
          onCancel={() => setBanTarget(null)}
          onConfirm={(comment) => void confirmBanIp(comment)}
        />
      ) : null}
    </div>
  );
}
